// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import {
  Building2, Pencil, Save, Loader2, AlertCircle, KeyRound, RotateCw,
  Copy, Upload, ShieldCheck, Check, HardDrive, FileText,
} from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

/**
 * /settings/pharmacy — pharmacy owner / admin self-service hub (WI-824).
 *
 * Shows the actor's own pharmacy and lets them:
 *   - Edit branding (display name override, primary color, logo)
 *   - Edit metadata (name, NPI, NCPDP, DEA, address, phone, email)
 *   - View their PioneerRx ingest credentials and rotate them (if Owner)
 *   - View their parent organization (if branch of a chain)
 *
 * Cannot edit:
 *   - Slug (would break webhook)
 *   - BAA reference / dates (uploaded as documents instead, by platform admin)
 *   - COO approval (recorded once at onboarding)
 *   - Parent organization, branch label
 *   - Onboarding state (suspend/reactivate is platform admin only)
 *
 * Permission gates:
 *   pharmacy.self.read         to view this page
 *   pharmacy.self.update       to edit metadata + branding
 *   pharmacy.self.rotate_creds to rotate ingest credentials
 */
export default function MyPharmacySettings() {
  const [data, setData] = useState<api.MyPharmacyResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revealedCreds, setRevealedCreds] = useState<api.CredentialsResponse | null>(null)

  async function reload() {
    setLoading(true); setError(null)
    try { setData(await api.getMyPharmacy()) }
    catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [])

  if (loading && !data) return <div style={loadingBox}><Loader2 size={16} className="spin" /> Loading…</div>
  if (error && !data)   return <div style={errorBox}><AlertCircle size={14} /> {error}</div>
  if (!data) return null

  const p = data.pharmacy

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>
          {p.logo_url
            ? <img src={p.logo_url} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'contain', verticalAlign: 'middle', marginRight: 10, border: '1px solid var(--color-border)', background: '#fff', padding: 2 }} />
            : <Building2 size={22} style={{ verticalAlign: '-3px', marginRight: 8 }} />
          }
          {p.display_name_override || p.name}
        </h1>
        {p.parent_organization_name && (
          <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
            Branch of <strong>{p.parent_organization_name}</strong>
            {p.branch_label && <> · {p.branch_label}</>}
          </div>
        )}
      </header>

      {error && <div style={errorBox}><AlertCircle size={14} /> {error}</div>}

      {revealedCreds && <CredsRevealCard creds={revealedCreds} onDismiss={() => setRevealedCreds(null)} />}

      <BrandingSelfSection pharmacy={p} onSaved={reload} />

      <StorageSelfSection />

      <DetailsSelfSection pharmacy={p} onSaved={reload} />

      <EmailConfigSelfSection onError={setError} />

      <CredentialsSelfSection
        webhookUrl={data.webhook_url}
        cred={data.active_credentials}
        canRotate={data.can_rotate_creds}
        onRotated={(r) => { setRevealedCreds(r); reload() }}
        onError={setError}
      />

      <Section title="Compliance documents" tone="info">
        <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-soft)', lineHeight: 1.5 }}>
          Your Business Associate Agreement and engagement approval are managed by your Owl Consultations administrator.
          {p.baa_expiry_date && (
            <> Your current BAA expires on <strong>{p.baa_expiry_date.substring(0, 10)}</strong>.</>
          )}
          {' '}If something needs updating, contact your Owl admin.
        </p>
      </Section>
    </div>
  )
}

/* ------------- Storage ------------- */

function fmtBytes(n: number): string {
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${n} B`
}

function StorageSelfSection() {
  const [s, setS] = useState<api.MyStorageResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.getMyStorage()
      .then(setS)
      .catch((e) => setErr(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [])

  const barColor = s?.over ? '#dc2626' : s?.warn ? '#d97706' : '#0d9488'
  const pct = s ? Math.max(0, Math.min(100, s.used_pct)) : 0

  return (
    <Section title="Storage">
      {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 size={13} className="spin" /> Loading…</div>}
      {err && <div style={{ fontSize: 13, color: '#dc2626' }}><AlertCircle size={13} /> {err}</div>}
      {s && (
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
            <HardDrive size={16} style={{ color: barColor, verticalAlign: '-2px' }} />
            <strong style={{ fontSize: 15 }}>{fmtBytes(s.patient_bytes)}</strong>
            <span style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>
              of {fmtBytes(s.quota_bytes)} attachment limit ({s.used_pct}%)
            </span>
          </div>
          <div style={{ height: 10, borderRadius: 6, background: '#f0efed', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: barColor, transition: 'width .3s' }} />
          </div>
          {s.over && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#dc2626' }}>
              <AlertCircle size={13} style={{ verticalAlign: '-2px' }} /> You're at your patient-attachment limit.{s.enforced ? ' New attachments are being blocked' : ' New attachments may be blocked'} — contact your Owl admin to increase the limit.
            </div>
          )}
          {!s.over && s.warn && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#d97706' }}>
              <AlertCircle size={13} style={{ verticalAlign: '-2px' }} /> You've used over 80% of your patient-attachment limit.
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--color-text-mute)' }}>
            Patient attachments {fmtBytes(s.patient_bytes)} (counted) · CareRx {fmtBytes(s.carerx_bytes)} + documents {fmtBytes(s.pharmacy_doc_bytes)} (not counted toward the limit)
          </div>
        </div>
      )}
    </Section>
  )
}


/* ------------- Branding ------------- */
function BrandingSelfSection({ pharmacy, onSaved }: {
  pharmacy: api.MyPharmacyResponse['pharmacy']
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [displayName, setDisplayName] = useState(pharmacy.display_name_override || '')
  const [color, setColor] = useState(pharmacy.primary_color_hex || '#0d9488')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [logoBusy, setLogoBusy] = useState(false)
  // D-CRX-77. The preview renders from activePharmacy — the SAME object the real generator reads —
  // so it shows what this tenant will actually print. Previewing from the settings form's own state
  // would only prove the form works.
  const [previewBusy, setPreviewBusy] = useState(false)

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.updateMyPharmacy({ displayNameOverride: displayName, primaryColorHex: color })
      setEditing(false); onSaved()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  async function uploadLogo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setLogoBusy(true); setError(null)
    try { await api.uploadPharmacyLogo(pharmacy.id, file); onSaved() }
    catch (err) { setError(friendlyError(err)) }
    finally { setLogoBusy(false); e.target.value = '' }
  }

  async function preview() {
    setPreviewBusy(true); setError(null)
    // D-CRX-80: rendered by the server now, through the same document pipeline the
    // patient's consent uses - the old client preview could drift from the real thing.
    try { await api.openCareRxLetterheadPreview() }
    catch (e) { setError(friendlyError(e)) }
    finally { setPreviewBusy(false) }
  }

  return (
    <Section
      title="Branding"
      action={!editing && (
        <button onClick={() => setEditing(true)} style={editBtnStyle}>
          <Pencil size={12} /> Edit
        </button>
      )}
    >
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-soft)' }}>
        Operator-facing only. Your team sees this when they log in. Patients and providers continue to see the standard portal branding.
      </p>
      {/* D-CRX-77: these fields also become the LETTERHEAD on every generated document, which is
          only visible once a real form is produced for a real patient. This renders the same
          template from the same values, with no patient and no writes, so a tenant can see what it
          will print before anyone signs anything. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 12px',
                    padding: '8px 11px', borderRadius: 9, border: '1px solid var(--color-border)' }}>
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', flex: '1 1 260px' }}>
          These values print as the letterhead on the enrollment form and every generated document.
          A field left blank prints blank &mdash; nothing is invented.
        </div>
        <button onClick={preview} disabled={previewBusy} style={{ ...editBtnStyle, cursor: previewBusy ? 'wait' : 'pointer' }}>
          {previewBusy ? <Loader2 size={12} className="spin" /> : <FileText size={12} />}
          {previewBusy ? 'Building…' : 'Preview letterhead'}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ width: 120 }}>
          <div style={logoBoxStyle}>
            {pharmacy.logo_url
              ? <img src={pharmacy.logo_url} alt="logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
              : <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>No logo</span>
            }
          </div>
          <label style={uploadLabelStyle}>
            {logoBusy ? <Loader2 size={11} className="spin" /> : <Upload size={11} />}
            {logoBusy ? 'Uploading…' : pharmacy.logo_url ? 'Replace' : 'Upload logo'}
            <input type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onChange={uploadLogo}
              disabled={logoBusy}
              style={{ display: 'none' }} />
          </label>
          <div style={{ fontSize: 10, color: 'var(--color-text-mute)', marginTop: 4, textAlign: 'center' }}>
            PNG, JPG, SVG, WebP · max 2 MB
          </div>
        </div>
        <div style={{ flex: 1 }}>
          {editing ? (
            <>
              <Field label="Display name override">
                <input value={displayName} onChange={e => setDisplayName(e.target.value)}
                  placeholder={pharmacy.name} style={inputStyle} />
              </Field>
              <Field label="Primary accent color">
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="color" value={color} onChange={e => setColor(e.target.value)}
                    style={{ width: 50, height: 32, padding: 0, border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer' }} />
                  <input value={color} onChange={e => setColor(e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'monospace', maxWidth: 130 }} />
                </div>
              </Field>
              {error && <div style={cardErrorBox}><AlertCircle size={13} /> {error}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={save} disabled={busy} style={primaryBtnStyle}>
                  {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
                </button>
                <button onClick={() => { setEditing(false); setDisplayName(pharmacy.display_name_override || ''); setColor(pharmacy.primary_color_hex || '#0d9488'); setError(null) }}
                  disabled={busy} style={secondaryBtnStyle}>Cancel</button>
              </div>
            </>
          ) : (
            <Grid>
              <Field label="Display name">
                {pharmacy.display_name_override || <span style={{ color: 'var(--color-text-mute)' }}>(legal name "{pharmacy.name}")</span>}
              </Field>
              <Field label="Primary color">
                {pharmacy.primary_color_hex
                  ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: pharmacy.primary_color_hex, border: '1px solid var(--color-border)' }} />
                      <code style={codeStyle}>{pharmacy.primary_color_hex}</code>
                    </span>
                  : <span style={{ color: 'var(--color-text-mute)' }}>Default</span>}
              </Field>
            </Grid>
          )}
        </div>
      </div>
    </Section>
  )
}

/* ------------- Details ------------- */

function DetailsSelfSection({ pharmacy, onSaved }: {
  pharmacy: api.MyPharmacyResponse['pharmacy']
  onSaved: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    name: pharmacy.name, npi: pharmacy.npi || '', ncpdp: pharmacy.ncpdp || '', dea: pharmacy.dea || '',
    email: pharmacy.email || '', primaryPhone: pharmacy.primary_phone || '',
    addressLine: pharmacy.address_line || '', city: pharmacy.city || '',
    stateCode: pharmacy.state_code || '', zipCode: pharmacy.zip_code || '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function set<K extends keyof typeof form>(k: K, v: string) { setForm(s => ({ ...s, [k]: v })) }

  async function save() {
    setBusy(true); setError(null)
    try {
      await api.updateMyPharmacy(form)
      setEditing(false); onSaved()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  return (
    <Section
      title="Pharmacy details"
      action={!editing && (
        <button onClick={() => setEditing(true)} style={editBtnStyle}>
          <Pencil size={12} /> Edit
        </button>
      )}
    >
      {editing ? (
        <>
          <Field label="Name *"><input value={form.name} onChange={e => set('name', e.target.value)} style={inputStyle} required /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <Field label="NPI"><input value={form.npi} onChange={e => set('npi', e.target.value)} style={inputStyle} /></Field>
            <Field label="NCPDP"><input value={form.ncpdp} onChange={e => set('ncpdp', e.target.value)} style={inputStyle} /></Field>
            <Field label="DEA"><input value={form.dea} onChange={e => set('dea', e.target.value)} style={inputStyle} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Email"><input type="email" value={form.email} onChange={e => set('email', e.target.value)} style={inputStyle} /></Field>
            <Field label="Phone"><input type="tel" value={form.primaryPhone} onChange={e => set('primaryPhone', e.target.value)} style={inputStyle} /></Field>
          </div>
          <Field label="Address"><input value={form.addressLine} onChange={e => set('addressLine', e.target.value)} style={inputStyle} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <Field label="City"><input value={form.city} onChange={e => set('city', e.target.value)} style={inputStyle} /></Field>
            <Field label="State"><input value={form.stateCode} onChange={e => set('stateCode', e.target.value.toUpperCase())} maxLength={2} style={inputStyle} /></Field>
            <Field label="Zip"><input value={form.zipCode} onChange={e => set('zipCode', e.target.value)} style={inputStyle} /></Field>
          </div>
          {error && <div style={cardErrorBox}><AlertCircle size={13} /> {error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={save} disabled={busy} style={primaryBtnStyle}>
              {busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
            </button>
            <button onClick={() => setEditing(false)} disabled={busy} style={secondaryBtnStyle}>Cancel</button>
          </div>
        </>
      ) : (
        <Grid>
          <Field label="Name">{pharmacy.name}</Field>
          <Field label="NPI">{pharmacy.npi || '—'}</Field>
          <Field label="NCPDP">{pharmacy.ncpdp || '—'}</Field>
          <Field label="DEA">{pharmacy.dea || '—'}</Field>
          <Field label="Email">{pharmacy.email || '—'}</Field>
          <Field label="Phone">{pharmacy.primary_phone || '—'}</Field>
          <Field label="Address">{[pharmacy.address_line, pharmacy.city, pharmacy.state_code, pharmacy.zip_code].filter(Boolean).join(', ') || '—'}</Field>
        </Grid>
      )}
    </Section>
  )
}

/* ------------- Credentials ------------- */

function CredentialsSelfSection({ webhookUrl, cred, canRotate, onRotated, onError }: {
  webhookUrl: string | null
  cred: api.MyPharmacyResponse['active_credentials']
  canRotate: boolean
  onRotated: (r: api.CredentialsResponse) => void
  onError: (e: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const allowlist = cred?.ip_allowlist ?? null
  if (!cred) {
    return (
      <Section title="PioneerRx ingest credentials">
        <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: 12, fontSize: 13, color: '#854d0e' }}>
          <ShieldCheck size={14} style={{ display: 'inline', verticalAlign: '-2px', marginRight: 6 }} />
          No credentials issued yet. Your Owl Consultations administrator will generate them as part of onboarding.
        </div>
      </Section>
    )
  }
  return (
    <Section title="PioneerRx ingest credentials">
      <Grid>
        <Field label="Webhook URL"><CopyValue value={webhookUrl} /></Field>
        <Field label="Username"><CopyValue value={cred.username} /></Field>
        <Field label="Generated">{new Date(cred.created_at).toLocaleString()}</Field>
        <Field label="Last used">{cred.last_used_at ? new Date(cred.last_used_at).toLocaleString() : 'Never'}</Field>
      </Grid>

      {/* IP allowlist — read-only here; managed by Owl Consultations (Network Security) */}
      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--color-border, #e5e7eb)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <ShieldCheck size={14} style={{ color: allowlist ? '#15803d' : 'var(--color-text-mute)' }} />
          <strong style={{ fontSize: 13 }}>Allowed source IPs</strong>
          {allowlist
            ? <span style={{ fontSize: 11, color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }}>Restricted</span>
            : <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: '1px 6px' }}>Any origin</span>}
        </div>
        <code style={{ ...codeStyle, fontSize: 12 }}>{allowlist || '— no restriction —'}</code>
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--color-text-mute)' }}>
          {allowlist
            ? 'Your PioneerRx feed only accepts data from the IP(s) above. Contact Owl Consultations to change this.'
            : 'Your PioneerRx feed currently accepts data from any origin. To restrict it to your pharmacy network, contact Owl Consultations with your static public IP.'}
        </p>
      </div>

      <div style={{ marginTop: 12 }}>
        {canRotate ? (
          <button
            onClick={async () => {
              if (!confirm('Rotating creates a new credential. The old one keeps working for 24 hours so PioneerRx can switch over. Continue?')) return
              setBusy(true)
              try { onRotated(await api.rotateMyIngestCredentials()) }
              catch (e) { onError(friendlyError(e)) }
              finally { setBusy(false) }
            }}
            disabled={busy}
            style={secondaryBtnStyle}
          >
            {busy ? <Loader2 size={13} className="spin" /> : <RotateCw size={13} />} Rotate credentials
          </button>
        ) : (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-mute)' }}>
            Only the pharmacy owner can rotate ingest credentials. Ask them or your Owl admin if you need a rotation.
          </p>
        )}
      </div>
    </Section>
  )
}

function EmailConfigSelfSection({ onError }: { onError: (e: string) => void }) {
  const [cfg, setCfg] = useState<api.EmailConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [form, setForm] = useState({ from_email: '', sender_name: '', reply_to: '', notifications_enabled: true })

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true)
    try {
      const c = await api.getMyEmailConfig()
      setCfg(c)
      setForm({
        from_email: c.from_email ?? '', sender_name: c.sender_name ?? '',
        reply_to: c.reply_to ?? '', notifications_enabled: c.notifications_enabled,
      })
    } catch (e) { onError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  async function save() {
    setBusy(true); setMsg(null)
    try {
      await api.putMyEmailConfig({
        from_email: form.from_email.trim() || null, sender_name: form.sender_name.trim() || null,
        reply_to: form.reply_to.trim() || null, notifications_enabled: form.notifications_enabled,
      })
      setMsg('Saved.'); setEditing(false); await load()
    } catch (e) { onError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  if (loading) return null

  return (
    <Section title="Email & notifications" action={!editing
      ? <button onClick={() => setEditing(true)} style={editBtnStyle}>Edit</button>
      : <button onClick={save} disabled={busy} style={editBtnStyle}>{busy ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save</button>}>
      <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: 10, fontSize: 12.5,
                    color: '#854d0e', marginBottom: 12, lineHeight: 1.5 }}>
        This sets the sender identity used for notifications and user/prescriber invitations. Actual
        delivery is enabled by your Owl Consultations administrator once outbound email transport is provisioned.
      </div>
      {msg && <div style={{ fontSize: 12.5, color: 'var(--color-success,#15803d)', marginBottom: 8 }}>{msg}</div>}
      {!editing ? (
        <Grid>
          <Field label="From address">{cfg?.from_email || <span style={{ color: 'var(--color-text-soft)' }}>not set</span>}</Field>
          <Field label="Sender name">{cfg?.sender_name || <span style={{ color: 'var(--color-text-soft)' }}>not set</span>}</Field>
          <Field label="Reply-to">{cfg?.reply_to || <span style={{ color: 'var(--color-text-soft)' }}>not set</span>}</Field>
          <Field label="Notifications">{cfg?.notifications_enabled ? 'Enabled' : 'Disabled'}</Field>
        </Grid>
      ) : (
        <Grid>
          <Field label="From address"><input type="email" value={form.from_email} placeholder="notifications@yourpharmacy.com"
            onChange={e => setForm({ ...form, from_email: e.target.value })} style={inputStyle} /></Field>
          <Field label="Sender name"><input value={form.sender_name} placeholder="Your Pharmacy"
            onChange={e => setForm({ ...form, sender_name: e.target.value })} style={inputStyle} /></Field>
          <Field label="Reply-to"><input type="email" value={form.reply_to}
            onChange={e => setForm({ ...form, reply_to: e.target.value })} style={inputStyle} /></Field>
          <Field label="Notifications">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input type="checkbox" checked={form.notifications_enabled}
                onChange={e => setForm({ ...form, notifications_enabled: e.target.checked })} />
              Send notification emails
            </label>
          </Field>
        </Grid>
      )}
    </Section>
  )
}

function CredsRevealCard({ creds, onDismiss }: { creds: api.CredentialsResponse; onDismiss: () => void }) {
  function copy(v: string) { navigator.clipboard.writeText(v) }
  return (
    <div style={{
      background: '#fffbeb', border: '2px solid #fbbf24', borderRadius: 8, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#854d0e', display: 'flex', gap: 6, alignItems: 'center' }}>
          <KeyRound size={15} /> New credentials generated
        </h3>
        <button onClick={onDismiss} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#854d0e', fontSize: 12, textDecoration: 'underline' }}>Dismiss</button>
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 12, color: '#854d0e' }}>{creds.warning}</p>
      <RevealRow label="Webhook" value={creds.webhook_url} onCopy={() => copy(creds.webhook_url)} />
      <RevealRow label="Username" value={creds.username} onCopy={() => copy(creds.username)} />
      <RevealRow label="Password" value={creds.password} mono onCopy={() => copy(creds.password)} />
      {creds.grace_period_until && (
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 8 }}>
          Old credential remains valid until {new Date(creds.grace_period_until).toLocaleString()}.
        </div>
      )}
    </div>
  )
}
function RevealRow({ label, value, mono, onCopy }: { label: string; value: string; mono?: boolean; onCopy: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', color: '#854d0e', minWidth: 80 }}>{label}</span>
      <code style={{ flex: 1, fontFamily: mono ? 'monospace' : 'inherit', background: '#fff', padding: '6px 10px', borderRadius: 4, border: '1px solid #fde68a', fontSize: 12, wordBreak: 'break-all' }}>
        {value}
      </code>
      <button onClick={() => { onCopy(); setCopied(true); setTimeout(() => setCopied(false), 1200) }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid #fbbf24', color: '#854d0e', padding: '4px 8px', fontSize: 11, borderRadius: 3, cursor: 'pointer' }}>
        <Copy size={12} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

/* ------------- Layout primitives (local) ------------- */

function Section({ title, action, children }: { title: string; tone?: 'info'; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--color-surface, #fff)',
      border: '1px solid var(--color-border)',
      borderRadius: 8, padding: 16, marginBottom: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  )
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  )
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>{children}</div>
}

// Read-only value with a copy button. Used for the webhook URL + username, which are long and
// frequently pasted into PioneerRx. The value wraps (no overflow) and the button sits beside it.
function CopyValue({ value }: { value: string | null }) {
  const [copied, setCopied] = useState(false)
  if (!value) return <code style={codeStyle}>—</code>
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
      <code style={{ ...codeStyle, flex: 1 }}>{value}</code>
      <button
        type="button"
        title="Copy"
        aria-label="Copy to clipboard"
        onClick={() => {
          navigator.clipboard?.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 1200)
        }}
        style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'transparent', border: '1px solid var(--color-border, #d1d5db)',
          color: copied ? '#15803d' : 'var(--color-text-soft, #4b5563)',
          padding: '3px 7px', fontSize: 11, borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )
}

const titleStyle: React.CSSProperties = { fontSize: 22, fontWeight: 700, margin: '0 0 4px', fontFamily: 'Georgia, serif' }
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', fontFamily: 'inherit', boxSizing: 'border-box',
}
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11, display: 'block', minWidth: 0, maxWidth: '100%',
  background: 'var(--color-surface-alt)', padding: '3px 6px', borderRadius: 3,
  wordBreak: 'break-all', overflowWrap: 'anywhere',
}
const primaryBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 14px', fontSize: 13, fontWeight: 500,
  background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const secondaryBtnStyle: React.CSSProperties = {
  ...primaryBtnStyle, background: 'transparent', color: 'var(--color-text-soft)', border: '1px solid var(--color-border)',
}
const editBtnStyle: React.CSSProperties = {
  ...secondaryBtnStyle, padding: '4px 10px', fontSize: 11,
}
const errorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginBottom: 12,
}
const cardErrorBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
  background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontSize: 12, marginTop: 8,
}
const loadingBox: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: 24,
  color: 'var(--color-text-soft)', fontSize: 14,
}
const logoBoxStyle: React.CSSProperties = {
  width: 120, height: 120,
  border: '1px dashed var(--color-border)', borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-surface-alt)', overflow: 'hidden',
}
const uploadLabelStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  marginTop: 6, padding: '4px 10px', fontSize: 11, fontWeight: 500,
  border: '1px solid var(--color-border)', borderRadius: 4, cursor: 'pointer',
  color: 'var(--color-text-soft)', width: '100%', boxSizing: 'border-box',
}
